import { PriceHistory, Resolution } from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useQueries } from '@tanstack/react-query';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useIsPriceHistoryV2Enabled } from '@terminal/services/price/price-history-flags';
import {
  fetchPrices,
  getEntityPriceVenue,
} from '@terminal/services/price/price-service';

const QUERY_KEY_PRICE_CHANGE_HISTORY = 'QUERY_KEY_PRICE_CHANGE_HISTORY';

interface UsePriceHistoryListProps {
  tags: string[];
  tickers: string[];
  dateFrom: string;
  dateTo: string;
  resolution: Resolution;
  enabled?: boolean;
}

export interface PriceHistoryListItem {
  data: PriceHistory;
  entityTag: string;
}

export interface UsePriceHistoryListResult {
  data: PriceHistoryListItem[];
  isLoading: boolean;
  isError: boolean;
}

export const usePriceHistoryList = ({
  tags,
  tickers,
  dateFrom,
  dateTo,
  resolution,
  enabled = true,
}: UsePriceHistoryListProps): UsePriceHistoryListResult => {
  // Resolve each entity's trading venue so v2 requests carry exchange/country
  // for non-US instruments (v1 ignores them). Entities are cached end-of-day,
  // so this is typically a cache read. `data` can drop entities that fail to
  // resolve, so key it by tag rather than relying on index alignment.
  const isPriceHistoryV2 = useIsPriceHistoryV2Enabled();
  const { data: entities, isFetched: areEntitiesFetched } = useEntities({
    entityTags: tags,
    // v1 ignores exchange/country, so only resolve venues when v2 is on —
    // avoids entity fetches the v1-only path never needed.
    enabled: enabled && isPriceHistoryV2,
  });
  const venueByTag = new Map(
    entities.map(entity => [entity.tag, getEntityPriceVenue(entity)])
  );

  // On v2, hold the price requests until the entities settle so we don't fire
  // an initial request with an undefined venue (the backend defaults these to
  // US) and then refetch once the entity arrives.
  const isWaitingForVenues = isPriceHistoryV2 && !areEntitiesFetched;

  return useQueries({
    queries: tags.map((entityTag, index) => {
      const venue = venueByTag.get(entityTag);

      // `areEntitiesFetched` only means every entity query settled — it's true
      // even when one errored or resolved without data, which drops it from
      // `venueByTag`. Gate each request on its own venue so a settled-but-
      // unresolved entity can't fire a US-defaulted v2 request for what may be
      // a non-US instrument.
      const hasResolvedVenue = !isPriceHistoryV2 || venueByTag.has(entityTag);

      return {
        queryKey: [
          QUERY_KEY_PRICE_CHANGE_HISTORY,
          entityTag,
          tickers[index],
          resolution,
          dateFrom,
          dateTo,
          // v1 ignores exchange/country, so only key on the venue when v2 is on
          // — otherwise a late venue resolution (once the entities settle)
          // churns the key and refetches the same v1 data.
          isPriceHistoryV2 ? venue?.exchange : undefined,
          isPriceHistoryV2 ? venue?.country : undefined,
          // Key on the flag too so a mid-session flag change can't reuse a
          // response from the other API version.
          isPriceHistoryV2,
        ],
        queryFn: () =>
          fetchPrices({
            entityTag,
            ticker: tickers[index],
            resolution,
            dateFrom,
            dateTo,
            exchange: venue?.exchange,
            country: venue?.country,
          }),
        staleTime: getTimeTillEndOfDayMs(),
        enabled: enabled && !isWaitingForVenues && hasResolvedVenue,
      };
    }),
    combine: (results): UsePriceHistoryListResult => ({
      data: results.flatMap((r, index) =>
        r.data ? [{ data: r.data, entityTag: tags[index] }] : []
      ),
      isLoading:
        (enabled && isWaitingForVenues) || results.some(r => r.isLoading),
      isError: results.every(r => r.isError),
    }),
  });
};
